import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Download } from 'lucide-react';
import { BULK_IMPORT_MAX_ROWS } from '@g17eco/emissions-config';

export interface SeedTemplateParams {
  rowCount: number;
  startYear: number;
  endYear: number;
}

interface SeedTemplateModalProps {
  isOpen: boolean;
  toggle: () => void;
  onDownload: (params: SeedTemplateParams) => void;
  isDownloading: boolean;
}

const MIN_ROWS = 1;
const MAX_ROWS = BULK_IMPORT_MAX_ROWS;
const DEFAULT_ROWS = 20;
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2015;
const MAX_YEAR = CURRENT_YEAR;

const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - MIN_YEAR + 1 }, (_, i) => CURRENT_YEAR - i);

export const SeedTemplateModal = ({ isOpen, toggle, onDownload, isDownloading }: SeedTemplateModalProps) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROWS);
  const [startYear, setStartYear] = useState(CURRENT_YEAR - 1);
  const [endYear, setEndYear] = useState(CURRENT_YEAR - 1);

  const isValid = rowCount >= MIN_ROWS && rowCount <= MAX_ROWS && Number.isInteger(rowCount)
    && startYear >= MIN_YEAR && startYear <= MAX_YEAR
    && endYear >= MIN_YEAR && endYear <= MAX_YEAR
    && startYear <= endYear;

  const handleDownload = () => {
    if (isValid) {
      onDownload({ rowCount, startYear, endYear });
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Download Seed Template</ModalHeader>
      <ModalBody>
        <p className='text-ThemeTextLight mb-3'>
          Generate a template pre-filled with realistic sample data for testing and demos.
          You can review and edit the data before importing.
        </p>

        <div className='mb-3'>
          <label className='form-label fw-medium' htmlFor='seed-row-count'>Number of rows</label>
          <input
            id='seed-row-count'
            type='number'
            className='form-control'
            value={rowCount}
            min={MIN_ROWS}
            max={MAX_ROWS}
            onChange={(e) => setRowCount(Number(e.target.value))}
          />
          <small className='text-ThemeTextLight'>
            {MIN_ROWS}–{MAX_ROWS} rows. Data cycles through valid fuel types, units, regions, etc.
          </small>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-medium'>Reporting period</label>
          <div className='d-flex gap-2'>
            <div className='flex-fill'>
              <label className='form-label text-ThemeTextLight small mb-1' htmlFor='seed-start-year'>Start year</label>
              <select
                id='seed-start-year'
                className='form-select'
                value={startYear}
                onChange={(e) => setStartYear(Number(e.target.value))}
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className='flex-fill'>
              <label className='form-label text-ThemeTextLight small mb-1' htmlFor='seed-end-year'>End year</label>
              <select
                id='seed-end-year'
                className='form-select'
                value={endYear}
                onChange={(e) => setEndYear(Number(e.target.value))}
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          {startYear > endYear && (
            <small className='text-danger'>Start year must be before or equal to end year</small>
          )}
          <small className='text-ThemeTextLight d-block mt-1'>
            Seed data will span the selected year range with varied period lengths
          </small>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-medium'>Industry preset</label>
          <select className='form-select' disabled>
            <option>Coming soon</option>
          </select>
          <small className='text-ThemeTextLight'>Tailored seed data for specific industries</small>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-medium'>Region preset</label>
          <select className='form-select' disabled>
            <option>Coming soon</option>
          </select>
          <small className='text-ThemeTextLight'>Focus seed data on a specific geographic region</small>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={toggle} disabled={isDownloading}>
          Cancel
        </Button>
        <Button onClick={handleDownload} disabled={isDownloading || !isValid}>
          {isDownloading
            ? <><span className='spinner-border spinner-border-sm me-1' />Generating…</>
            : <><Download className='tw:size-4 tw:mr-1' />Download Seed Template</>
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};
